import { downlineRevenue } from "../../utils/downlineRevenue";
import React, { useState } from "react";
import {
  Table,
  Text,
  Group,
  Stack,
  Button,
  Box,
  ActionIcon,
  Avatar,
  Badge,
  ScrollArea,
  Switch,
  Modal,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconEye,
  IconMail,
  IconUsers,
  IconUser,
  IconCurrencyDollar,
} from "@tabler/icons-react";

const formatCurrency = value => {
  if (value === null || value === undefined || value === "") return "—";
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numValue);
};

const formatPercentage = value => {
  if (value === null || value === undefined || value === "") return "—";
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "—";
  const formatted =
    numValue % 1 === 0 ? numValue.toFixed(0) : numValue.toFixed(1);
  return formatted + "%";
};

const formatDate = value => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  const day = d.getDate();
  const mod10 = day % 10;
  const mod100 = day % 100;
  const suffix =
    mod10 === 1 && mod100 !== 11
      ? "st"
      : mod10 === 2 && mod100 !== 12
        ? "nd"
        : mod10 === 3 && mod100 !== 13
          ? "rd"
          : "th";
  const month = d.toLocaleDateString("en-US", { month: "long" });
  return `${month} ${day}${suffix}`;
};

const REP_TYPE_COLORS = {
  veteran: "violet",
  rookie: "orange",
  senior: "blue",
  manager: "teal",
};

const getRepTypeColor = name => {
  if (!name) return "gray";
  return REP_TYPE_COLORS[name.toLowerCase()] || "blue";
};

const getRepProfileImage = rep =>
  rep.profile_image_url || rep.profile_image || rep.avatar_url || undefined;

const getRepInitials = rep =>
  `${rep.first_name?.charAt(0)?.toUpperCase() || ""}${rep.last_name?.charAt(0)?.toUpperCase() || ""}` ||
  rep.rep_name
    ?.split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(name => name.charAt(0).toUpperCase())
    .join("") ||
  "R";

const TREE_LINE_COLOR = "#ced4da";

// Avatar size used in the Name column
const AVATAR_SIZE = 60;
// Left padding applied to every td in the Name column (overrides Mantine default)
const CELL_LEFT_PADDING = 16;
// Per-level indent.  Must be > AVATAR_SIZE (60) so there is a visible
// horizontal gap between the right edge of the parent avatar and the left
// edge of the child row.  80 px gives a 20 px connector span per level.
const CONNECTOR_WIDTH = 80; // px

// X position of the HORIZONTAL CENTRE of the avatar at depth i.
// All branch lines (vertical pass-through, horizontal elbow, downward stub)
// are anchored here so they align with the avatar's visual centre.
const avatarCenterX = i =>
  i * CONNECTOR_WIDTH + CELL_LEFT_PADDING + AVATAR_SIZE / 2;

/**
 * Returns CSS background-* styles that draw tree-connector lines painted
 * DIRECTLY on the <td> element so they span its full height (including
 * padding) and never have gaps between rows.
 *
 * lineX at depth i = avatarCenterX(i)
 * This aligns the vertical line with the horizontal centre of the Avatar
 * rendered in a row at that depth.
 *
 * ancestorIsLast[i] = true  →  ancestor at depth i was the last sibling,
 * no continuing vertical line for that column.
 */
const buildTreeLineBackground = (level, isLastInGroup, ancestorIsLast) => {
  if (level === 0) return {};

  const images = [];
  const sizes = [];
  const positions = [];

  // Horizontal branch: from center of parent avatar → left edge of this avatar
  // Stops at the circle boundary so it doesn't visually run through the avatar.
  const hStartX = avatarCenterX(level - 1);
  const hEndX = avatarCenterX(level) - AVATAR_SIZE / 2; // left edge of child
  const hWidth = hEndX - hStartX; // = CONNECTOR_WIDTH - AVATAR_SIZE/2 = 50 px
  images.push(`linear-gradient(${TREE_LINE_COLOR}, ${TREE_LINE_COLOR})`);
  sizes.push(`${hWidth}px 2px`);
  positions.push(`${hStartX}px 50%`);

  // Vertical lines for each depth 0..level-1
  for (let i = 0; i < level; i++) {
    const isInnermost = i === level - 1;

    // Never draw the root (level-0) column line on rows deeper than level 1.
    // That prevents the top-level manager's vertical line from bleeding through
    // into nested sub-trees.
    if (i === 0 && level >= 2) continue;

    // Non-innermost ancestors: skip the column line only when the *next-level*
    // ancestor (index i+1) was last in its group — meaning there are no more
    // siblings at depth i+1 below this row and no line is needed at column i.
    // Using ancestorIsLast[i] was wrong: it checked whether ancestor i was the
    // last sibling of ITS parent, which is irrelevant for rows inside its own
    // sub-tree (e.g. a rep being last under their manager should not suppress
    // their column line inside their own expanded children/grandchildren).
    if (!isInnermost && ancestorIsLast[i + 1]) continue;

    const continueVertical = isInnermost ? !isLastInGroup : true;
    const lineX = avatarCenterX(i) - 1;

    images.push(`linear-gradient(${TREE_LINE_COLOR}, ${TREE_LINE_COLOR})`);
    sizes.push(`2px ${continueVertical ? "100%" : "50%"}`);
    positions.push(`${lineX}px 0`);
  }

  return {
    backgroundImage: images.join(", "),
    backgroundSize: sizes.join(", "),
    backgroundPosition: positions.join(", "),
    backgroundRepeat: "no-repeat",
  };
};

/**
 * When a parent row is expanded we need to draw a downward connector from the
 * avatar's bottom edge to the cell bottom.  We CANNOT do this with a CSS
 * background gradient on the <td> because the Avatar uses radius="xl" which
 * clips it to a circle — the transparent corners of the avatar's bounding box
 * let the background show straight through the middle of the avatar.
 *
 * Instead we only return the ancestor tree lines (same as a normal row) and
 * render a real DOM element for the downward stub in the JSX below.
 */
const buildExpandedParentBackground = (
  level,
  isLastInGroup,
  ancestorIsLast
) => {
  // Ancestor lines only — no downward extension here.
  return buildTreeLineBackground(level, isLastInGroup, ancestorIsLast);
};

const RepRows = ({
  reps,
  level = 0,
  ancestorIsLast = [],
  showPayDetails,
  payMode = "current",
  onResendInvite,
  onViewDetails,
  onPrefetch,
  onViewPayDetails,
  onPitchReview,
  resendingInvite,
  onSetBackendEligible,
  setPendingBackendToggle,
}) => {
  const [expandedIds, setExpandedIds] = useState({});

  const toggleExpand = id => {
    setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const rows = [];

  reps.forEach((rep, index) => {
    const hasSubReps = rep.reps && rep.reps.length > 0;
    const isExpanded = !!expandedIds[rep.rep_id];
    const isLastInGroup = index === reps.length - 1;

    const repTypeName = rep.rep_type?.name || rep.role_name || null;

    // The figures for the mode showing. Projected = the season projection;
    // current = revenue on the books so far (FieldRoutes sales plus manually
    // recorded revenue) and the earnings the engine has computed on it.
    const isCurrent = payMode === "current";
    const revenueField = isCurrent ? "total_revenue" : "projected_revenue";
    const personalRevenue = rep[revenueField];
    const teamRevenue = hasSubReps ? downlineRevenue(rep, revenueField) : null;
    const marginPct = isCurrent
      ? rep.current_manager_percentage
      : rep.projected_manager_percentage;
    const marginEarnings = isCurrent
      ? rep.current_manager_earnings
      : rep.projected_manager_earnings;
    // The caller's take off everyone under this rep: each descendant's
    // contribution to the caller, summed down the tree; margin is that over
    // the downline revenue. Null until the PIN is entered (the fields are
    // withheld), or when the rep has nobody under them.
    const managerEarningsField = isCurrent
      ? "current_manager_earnings"
      : "projected_manager_earnings";
    const downlineTake = hasSubReps
      ? downlineRevenue(rep, managerEarningsField)
      : null;
    const downlinePayWithheld =
      hasSubReps &&
      (rep.reps || []).every(c => c?.[managerEarningsField] == null);
    const downlineEarnings = downlinePayWithheld ? null : downlineTake;
    const downlineMarginPct =
      downlineEarnings == null || !teamRevenue
        ? null
        : (downlineEarnings / teamRevenue) * 100;
    const repEarnings = isCurrent
      ? rep.rep_commission_earnings
      : rep.rep_projected_commission_earnings;
    const repEarningsPct = isCurrent
      ? rep.rep_commission_percent
      : rep.rep_projected_commission_percent;
    // What the rep earns off their own downline: the override and marketing
    // deal earnings credited to them. Total = that plus their own commission.
    const num = v => (v == null ? null : Number(v) || 0);
    const overrideEarnings = num(
      isCurrent
        ? rep.current_override_earnings
        : rep.projected_override_earnings
    );
    const mdEarnings = num(
      isCurrent ? rep.current_md_earnings : rep.projected_md_earnings
    );
    const repDownlineEarnings =
      overrideEarnings == null && mdEarnings == null
        ? null
        : (overrideEarnings || 0) + (mdEarnings || 0);
    const repTotalEarnings =
      repEarnings == null && repDownlineEarnings == null
        ? null
        : (num(repEarnings) || 0) + (repDownlineEarnings || 0);
    // The rep's own margin on their downline: their downline earnings over
    // the downline revenue when they have a team, else the average override
    // percentage credited to them (a rep paid on recruits without an MD).
    const overrideAvgPct = num(
      isCurrent
        ? rep.current_override_percentage
        : rep.projected_override_percentage
    );
    const repDownlineMarginPct =
      repDownlineEarnings != null && teamRevenue
        ? (repDownlineEarnings / teamRevenue) * 100
        : null;
    const repDownlinePct =
      repDownlineMarginPct != null
        ? repDownlineMarginPct
        : overrideAvgPct
          ? overrideAvgPct
          : null;

    // Expanded parent rows get a downward extension line from their avatar;
    // all other rows just get the ancestor connector lines.
    const treeLineBg =
      hasSubReps && isExpanded
        ? buildExpandedParentBackground(level, isLastInGroup, ancestorIsLast)
        : buildTreeLineBackground(level, isLastInGroup, ancestorIsLast);

    rows.push(
      <Table.Tr
        key={rep.rep_id}
        // Warm the rep-details cache on hover so the drawer opens instantly.
        onMouseEnter={() => onPrefetch?.(rep.rep_id)}
      >
        {/* Name — tree lines are CSS backgrounds on this td.
            The downward connector for expanded parent rows is anchored inside
            a position:relative wrapper div around the Avatar — this is the
            only reliable containing block (td positioning is inconsistent
            across browsers with Mantine's table styles). */}
        <Table.Td
          style={{
            ...treeLineBg,
            paddingLeft: level * CONNECTOR_WIDTH + CELL_LEFT_PADDING,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Downward connector for expanded parents — anchored directly to
              the <td> (position:relative) so overflow:hidden clips it cleanly
              at the exact cell boundary with no sub-pixel gaps.
              top:"50%" aligns with the avatar's vertical centre; the solid
              circular avatar hides the line where it overlaps, so only the
              lower half (avatar-centre → cell-bottom) is visible. */}
          {hasSubReps && isExpanded && (
            <div
              style={{
                position: "absolute",
                // Start at the BOTTOM EDGE of the avatar circle:
                // cell-midpoint (50%) + avatar-radius (AVATAR_SIZE/2)
                top: `calc(50% + ${AVATAR_SIZE / 2}px)`,
                left: avatarCenterX(level) - 1,
                width: 2,
                // Fill from avatar bottom to cell bottom
                height: `calc(50% - ${AVATAR_SIZE / 2}px)`,
                background: TREE_LINE_COLOR,
                pointerEvents: "none",
              }}
            />
          )}
          <Group gap="sm" wrap="nowrap">
            {/* The avatar itself opens the rep's details drawer */}
            <div
              style={{
                position: "relative",
                flexShrink: 0,
                cursor: onViewDetails ? "pointer" : undefined,
              }}
              onClick={
                onViewDetails ? () => onViewDetails(rep.rep_id, rep) : undefined
              }
              role={onViewDetails ? "button" : undefined}
              tabIndex={onViewDetails ? 0 : undefined}
              onKeyDown={
                onViewDetails
                  ? e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onViewDetails(rep.rep_id, rep);
                      }
                    }
                  : undefined
              }
              aria-label={
                onViewDetails
                  ? `View details for ${rep.first_name} ${rep.last_name}`
                  : undefined
              }
            >
              <Avatar
                src={getRepProfileImage(rep)}
                size={AVATAR_SIZE}
                radius="xl"
                color="orange"
              >
                {getRepInitials(rep)}
              </Avatar>
            </div>
            <Stack gap={4}>
              <Group gap="xs" align="center">
                <Text fz="lg" fw={400}>
                  {rep.first_name} {rep.last_name}
                </Text>
                {repTypeName && (
                  <Badge
                    variant="light"
                    color={getRepTypeColor(repTypeName)}
                    size="sm"
                    radius="sm"
                  >
                    {repTypeName}
                  </Badge>
                )}
              </Group>
              <Switch
                size="xs"
                label="Backend eligible"
                checked={rep.backend_eligible !== false}
                disabled={!onSetBackendEligible}
                onChange={e => {
                  const eligible = e.currentTarget.checked;
                  setPendingBackendToggle({ rep, eligible });
                }}
                styles={{ label: { fontSize: 12, paddingLeft: 6 } }}
              />
            </Stack>
          </Group>
        </Table.Td>

        {/* Team Size — hidden with pay details on, to make room */}
        {!showPayDetails && (
          <Table.Td>
            <Group gap="sm" align="center" wrap="nowrap">
              <Avatar
                size={44}
                radius="xl"
                color={rep.rep_count > 0 ? "blue" : "gray"}
                variant="light"
              >
                <Text fz="md" fw={400}>
                  {rep.rep_count || 0}
                </Text>
              </Avatar>
              <Stack gap={0}>
                {rep.rep_count > 0 ? (
                  <>
                    <Text fz="md" fw={400}>
                      Reps
                    </Text>
                    <Text size="xs" c="dimmed">
                      Recruited
                    </Text>
                  </>
                ) : (
                  <>
                    <Text fz="md" fw={400}>
                      No recruits
                    </Text>
                    <Text size="xs" c="dimmed">
                      Yet
                    </Text>
                  </>
                )}
              </Stack>
            </Group>
          </Table.Td>
        )}

        {/* Revenue — personal on top, downline underneath. The pair is
            centered as a block but left-aligned inside it, so the two icons
            stack in a column and both figures start at the same x. */}
        <Table.Td ta="center">
          <Stack gap={6} align="center">
            {/* Fixed width so the block sits at the same x in every row and
                the icon column lines up down the whole table. */}
            <Stack gap={6} align="flex-start" style={{ width: 150 }}>
              <Group gap="xs" align="center" wrap="nowrap">
                <Avatar size={32} radius="xl" color="blue" variant="light">
                  <IconUser size={16} />
                </Avatar>
                <Text fz="md" fw={500}>
                  {formatCurrency(personalRevenue)}
                </Text>
              </Group>
              <Group gap="xs" align="center" wrap="nowrap">
                <Avatar size={32} radius="xl" color="teal" variant="light">
                  <IconUsers size={16} />
                </Avatar>
                <Text fz="md" fw={500} c={hasSubReps ? undefined : "dimmed"}>
                  {hasSubReps ? formatCurrency(teamRevenue) : "—"}
                </Text>
              </Group>
            </Stack>
          </Stack>
        </Table.Td>

        {showPayDetails ? (
          <>
            {/* Margin — the caller's margin on this rep's personal revenue,
                then on their downline's revenue */}
            <Table.Td ta="center">
              <Stack gap={6} align="center">
                <Text fz="md" fw={500}>
                  {marginPct == null ? "—" : formatPercentage(marginPct)}
                </Text>
                <Text fz="md" fw={500} c={hasSubReps ? undefined : "dimmed"}>
                  {downlineMarginPct == null
                    ? "—"
                    : formatPercentage(downlineMarginPct)}
                </Text>
              </Stack>
            </Table.Td>
            {/* Earnings — what the caller makes off this rep's personal
                revenue, then off their downline */}
            <Table.Td ta="center">
              <Stack gap={6} align="center">
                <Text fz="md" fw={500}>
                  {marginEarnings == null
                    ? "—"
                    : formatCurrency(marginEarnings)}
                </Text>
                <Text fz="md" fw={500} c={hasSubReps ? undefined : "dimmed"}>
                  {downlineEarnings == null
                    ? "—"
                    : formatCurrency(downlineEarnings)}
                </Text>
              </Stack>
            </Table.Td>

            {/* Rep Personal Earnings — the rep's own commission, with the
                scale percentage they sit on underneath */}
            <Table.Td ta="center">
              <Stack gap={6} align="center">
                <Text fz="md" fw={500}>
                  {repEarnings == null ? "—" : formatCurrency(repEarnings)}
                </Text>
                <Text fz="md" fw={500}>
                  {repEarningsPct == null
                    ? "—"
                    : formatPercentage(repEarningsPct)}
                </Text>
              </Stack>
            </Table.Td>

            {/* Rep Downline Earnings — override + marketing deal credited to
                the rep, with their margin on that downline underneath */}
            <Table.Td ta="center">
              <Stack gap={6} align="center">
                <Text fz="md" fw={500}>
                  {repDownlineEarnings == null
                    ? "—"
                    : formatCurrency(repDownlineEarnings)}
                </Text>
                <Text fz="md" fw={500}>
                  {repDownlinePct == null
                    ? "—"
                    : formatPercentage(repDownlinePct)}
                </Text>
              </Stack>
            </Table.Td>

            {/* Total Rep Earnings — personal + downline */}
            <Table.Td ta="center">
              <Text fz="md" fw={600}>
                {repTotalEarnings == null
                  ? "—"
                  : formatCurrency(repTotalEarnings)}
              </Text>
            </Table.Td>
          </>
        ) : (
          <Table.Td>
            <Text fz="lg" fw={400}>
              {formatDate(rep.start_date)}
            </Text>
          </Table.Td>
        )}

        {/* Actions — with pay details on the buttons collapse to an eye icon
            (opens the rep's details) plus the expand/collapse chevron, so
            nested reps can still be opened */}
        <Table.Td style={{ whiteSpace: "nowrap", verticalAlign: "middle" }}>
          <Group
            gap="xs"
            wrap="nowrap"
            justify="flex-end"
            align="center"
            style={{ flexWrap: "nowrap", whiteSpace: "nowrap" }}
          >
            {!showPayDetails && !rep.verified && onResendInvite && (
              <Button
                variant="outline"
                color="orange"
                size="xs"
                leftSection={<IconMail size={14} />}
                onClick={() => onResendInvite(rep.rep_id)}
                disabled={resendingInvite === rep.rep_id}
                loading={resendingInvite === rep.rep_id}
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                {resendingInvite === rep.rep_id
                  ? "Sending..."
                  : "Resend Invite"}
              </Button>
            )}
            {!showPayDetails && onViewDetails && (
              <Button
                variant="outline"
                color="orange"
                size="xs"
                leftSection={<IconEye size={14} />}
                onClick={() => onViewDetails(rep.rep_id, rep)}
                style={{
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                View Details
              </Button>
            )}
            {!showPayDetails && onViewPayDetails && (
              <Button
                variant="outline"
                color="orange"
                size="xs"
                leftSection={<IconCurrencyDollar size={14} />}
                onClick={() => onViewPayDetails(rep.rep_id)}
                style={{
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                View Pay Details
              </Button>
            )}
            {showPayDetails && onViewDetails && (
              <ActionIcon
                variant="subtle"
                color="orange"
                size="sm"
                onClick={() => onViewDetails(rep.rep_id, rep)}
                aria-label="View details"
                title="View details"
                style={{ alignSelf: "center" }}
              >
                <IconEye size={14} />
              </ActionIcon>
            )}
            {hasSubReps && (
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => toggleExpand(rep.rep_id)}
                style={{ alignSelf: "center" }}
              >
                {isExpanded ? (
                  <IconChevronDown size={14} />
                ) : (
                  <IconChevronRight size={14} />
                )}
              </ActionIcon>
            )}
            {!hasSubReps && (
              <Box
                style={{
                  width: 22,
                  height: 22,
                  visibility: "hidden",
                  flexShrink: 0,
                }}
              />
            )}
          </Group>
        </Table.Td>
      </Table.Tr>
    );

    if (hasSubReps && isExpanded) {
      rows.push(
        <RepRows
          key={`sub-${rep.rep_id}`}
          reps={rep.reps}
          level={level + 1}
          ancestorIsLast={[...ancestorIsLast, isLastInGroup]}
          showPayDetails={showPayDetails}
          payMode={payMode}
          onResendInvite={onResendInvite}
          onViewDetails={onViewDetails}
          onPrefetch={onPrefetch}
          onViewPayDetails={onViewPayDetails}
          onPitchReview={onPitchReview}
          resendingInvite={resendingInvite}
          onSetBackendEligible={onSetBackendEligible}
          setPendingBackendToggle={setPendingBackendToggle}
        />
      );
    }
  });

  return rows;
};

const RepCardList = ({
  reps,
  showPayDetails = false,
  payMode = "current",
  onResendInvite,
  onViewDetails,
  onPrefetch,
  onViewPayDetails,
  onPitchReview,
  resendingInvite,
  onSetBackendEligible,
}) => {
  // The rep whose backend-eligibility switch was just flipped, held until the
  // manager confirms in the dialog; the switch itself only changes on confirm.
  const [pendingBackendToggle, setPendingBackendToggle] = useState(null);
  const [backendDialogOpen, setBackendDialogOpen] = useState(false);
  const [savingBackendToggle, setSavingBackendToggle] = useState(false);

  const openBackendToggle = pending => {
    setPendingBackendToggle(pending);
    setBackendDialogOpen(true);
  };
  // Closing only hides the dialog; the rep's name stays in the text while it
  // fades out instead of flashing the other title.
  const closeBackendToggle = () => setBackendDialogOpen(false);

  const confirmBackendToggle = async () => {
    if (!pendingBackendToggle || !onSetBackendEligible) return;
    const { rep, eligible } = pendingBackendToggle;
    try {
      setSavingBackendToggle(true);
      await onSetBackendEligible(rep, eligible);
      closeBackendToggle();
    } finally {
      setSavingBackendToggle(false);
    }
  };

  const headerCellStyle = {
    background: "#fafbfc",
    borderTop: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  };
  const headerFirstCellStyle = {
    ...headerCellStyle,
    minWidth: 220,
  };
  const headerMiddleCellStyle = headerCellStyle;
  const modeLabel = payMode === "current" ? "Current" : "Projected";
  const headerLastCellStyle = {
    ...headerCellStyle,
  };

  if (!reps || reps.length === 0) {
    return (
      <Box ta="center" py="xl">
        <Text c="dimmed">No representatives found.</Text>
      </Box>
    );
  }

  return (
    <>
      <Modal
        opened={backendDialogOpen}
        onClose={closeBackendToggle}
        title={
          pendingBackendToggle?.eligible
            ? "Make backend eligible?"
            : "Remove backend eligibility?"
        }
        centered
      >
        {pendingBackendToggle && (
          <Stack gap="md">
            <Text size="sm">
              {pendingBackendToggle.eligible
                ? `${pendingBackendToggle.rep.first_name} ${pendingBackendToggle.rep.last_name} will earn their own commission on their revenue again, and the managers above go back to earning only their usual margin on it.`
                : `${pendingBackendToggle.rep.first_name} ${pendingBackendToggle.rep.last_name} will earn no backend on their revenue. Their revenue rolls up in full: the manager directly above keeps their whole marketing-deal percentage on it, and each level above still earns only its increment.`}
            </Text>
            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={closeBackendToggle}
                disabled={savingBackendToggle}
              >
                Cancel
              </Button>
              <Button
                color={pendingBackendToggle.eligible ? "green" : "red"}
                onClick={confirmBackendToggle}
                loading={savingBackendToggle}
              >
                {pendingBackendToggle.eligible
                  ? "Make eligible"
                  : "Remove eligibility"}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
      <ScrollArea>
        <Table
          highlightOnHover
          verticalSpacing="md"
          horizontalSpacing="lg"
          stickyHeader
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={headerFirstCellStyle}>Name</Table.Th>
              {!showPayDetails && (
                <Table.Th style={headerMiddleCellStyle}>Team Size</Table.Th>
              )}
              <Table.Th style={headerMiddleCellStyle} ta="center">
                <Stack gap={0} align="center">
                  <Text size="xs" c="dimmed" fw={500}>
                    {modeLabel}
                  </Text>
                  <span>Revenue</span>
                </Stack>
              </Table.Th>
              {showPayDetails ? (
                <>
                  <Table.Th style={headerMiddleCellStyle} ta="center">
                    <Stack gap={0} align="center">
                      <Text size="xs" c="dimmed" fw={500}>
                        {modeLabel}
                      </Text>
                      <span>Margin</span>
                    </Stack>
                  </Table.Th>
                  <Table.Th style={headerMiddleCellStyle} ta="center">
                    <Stack gap={0} align="center">
                      <Text size="xs" c="dimmed" fw={500}>
                        {modeLabel}
                      </Text>
                      <span>Earnings</span>
                    </Stack>
                  </Table.Th>
                  <Table.Th style={headerMiddleCellStyle} ta="center">
                    <Stack gap={0} align="center">
                      <Text size="xs" c="dimmed" fw={500}>
                        {modeLabel}
                      </Text>
                      <span>Rep Personal Earnings</span>
                    </Stack>
                  </Table.Th>
                  <Table.Th style={headerMiddleCellStyle} ta="center">
                    <Stack gap={0} align="center">
                      <Text size="xs" c="dimmed" fw={500}>
                        {modeLabel}
                      </Text>
                      <span>Rep Downline Earnings</span>
                    </Stack>
                  </Table.Th>
                  <Table.Th style={headerMiddleCellStyle} ta="center">
                    <Stack gap={0} align="center">
                      <Text size="xs" c="dimmed" fw={500}>
                        {modeLabel}
                      </Text>
                      <span>Total Rep Earnings</span>
                    </Stack>
                  </Table.Th>
                </>
              ) : (
                <Table.Th style={headerMiddleCellStyle}>Start Date</Table.Th>
              )}
              <Table.Th
                style={{
                  ...headerLastCellStyle,
                  paddingLeft: showPayDetails ? undefined : 65,
                }}
                ta="center"
              >
                {showPayDetails ? "" : "Actions"}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <RepRows
              reps={reps}
              level={0}
              ancestorIsLast={[]}
              showPayDetails={showPayDetails}
              payMode={payMode}
              onResendInvite={onResendInvite}
              onViewDetails={onViewDetails}
              onPrefetch={onPrefetch}
              onViewPayDetails={onViewPayDetails}
              onPitchReview={onPitchReview}
              resendingInvite={resendingInvite}
              onSetBackendEligible={onSetBackendEligible}
              setPendingBackendToggle={openBackendToggle}
            />
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </>
  );
};

export default RepCardList;
